import { useRef, useEffect, useCallback } from 'react';
import ChatMessage from './ChatMessage';
import LoadingIndicator from './LoadingIndicator';
import EmptyState from './EmptyState';
import PropTypes from 'prop-types';

const ChatContainer = ({ messages, isLoading, onSuggestionClick }) => {
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Listen for section expansion events to trigger scroll
  useEffect(() => {
    const handleSectionExpand = () => {
      // Small delay to let the DOM update before scrolling
      setTimeout(scrollToBottom, 100);
    };

    window.addEventListener('chatSectionExpanded', handleSectionExpand);
    return () => {
      window.removeEventListener('chatSectionExpanded', handleSectionExpand);
    };
  }, [scrollToBottom]);

  return (
    <div className="flex-1 overflow-y-auto relative bg-white">
      {messages.length === 0 && !isLoading ? (
        <EmptyState onSuggestionClick={onSuggestionClick} />
      ) : (
        <div className="absolute inset-0">
          {/* Chat Messages */}
          <div className="w-full p-6 space-y-6">
            {messages.map((message, index) => (
              <ChatMessage
                key={`${message.threadId || 'default'}-${index}-${message.type}-${message.content?.substring(0, 20)}`}
                {...message}
                isLoading={isLoading && index === messages.length - 1}
              />
            ))}
            {isLoading && <LoadingIndicator stage="thinking" />}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}
    </div>
  );
};

ChatContainer.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      content: PropTypes.string.isRequired,
      rawResponse: PropTypes.object,
      threadId: PropTypes.string,
      isHistoryUpdated: PropTypes.bool
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  onSuggestionClick: PropTypes.func
};

ChatContainer.defaultProps = {
  messages: [],
  isLoading: false
};

export default ChatContainer; 